// Business Dashboard - Diseño Profesional Completo
import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  MessageSquare, Clock, Users, BarChart3, Settings, Bot, User, Send,
  CheckCircle, XCircle, Play, Pause, TrendingUp, Save, TestTube,
  Headphones, X, HelpCircle, Plus, AlertCircle, FileText, ChevronRight,
  Eye, Upload, Camera, RefreshCw, Zap, Activity, Shield, Star,
  ChevronDown, ChevronUp, Search, Filter, Bell, Mail, Phone,
  Tag, Flag, ArrowUpRight, MoreHorizontal, Edit3, Trash2, Copy,
  Calendar, Lock, Unlock, Sparkles, Check, Key, Image, Globe,
  ToggleLeft, ToggleRight, Ticket, Circle, Timer, Hash, Layers,
  Target, ChevronLeft, Inbox, Archive, SortAsc, TrendingDown,
  AlertTriangle, Info, Wrench, Database, Code, MessageCircle,
  BarChart2, PieChart, LineChart, Download, RotateCcw, Cpu, Crown
} from 'lucide-react';
import PlansModule from './PlansModule';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// ─── Helpers ──────────────────────────────────────────────────────────────────
const STATUS_CONFIG = {
  open:         { label: 'Abierto',        color: 'text-yellow-400', bg: 'bg-yellow-500/15 border-yellow-500/30' },
  in_progress:  { label: 'En Progreso',    color: 'text-blue-400',   bg: 'bg-blue-500/15 border-blue-500/30' },
  waiting_user: { label: 'Esperando',      color: 'text-purple-400', bg: 'bg-purple-500/15 border-purple-500/30' },
  resolved:     { label: 'Resuelto',       color: 'text-green-400',  bg: 'bg-green-500/15 border-green-500/30' },
  closed:       { label: 'Cerrado',        color: 'text-gray-400',   bg: 'bg-gray-500/15 border-gray-500/30' },
};
const PRIORITY_CONFIG = {
  low:    { label: 'Baja',     color: 'text-gray-400',   dot: 'bg-gray-400' },
  normal: { label: 'Normal',   color: 'text-blue-400',   dot: 'bg-blue-400' },
  high:   { label: 'Alta',     color: 'text-orange-400', dot: 'bg-orange-400' },
  urgent: { label: 'Urgente',  color: 'text-red-400',    dot: 'bg-red-400' },
};
const fmtTime = (ts) => {
  if (!ts) return '';
  const d = new Date(ts);
  const now = new Date();
  const diff = (now - d) / 1000;
  if (diff < 60) return 'Ahora';
  if (diff < 3600) return `${Math.floor(diff/60)}m`;
  if (diff < 86400) return `${Math.floor(diff/3600)}h`;
  return d.toLocaleDateString('es-MX', { day:'numeric', month:'short' });
};
const avatar = (name='?') => name[0]?.toUpperCase() || '?';
const COLORS = ['#ef4444','#8b5cf6','#3b82f6','#10b981','#f59e0b','#ec4899','#06b6d4'];

// ─── Mini Bar Chart ────────────────────────────────────────────────────────────
function MiniBar({ value, max, color = '#ef4444' }) {
  const pct = max > 0 ? (value / max) * 100 : 0;
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-1.5 bg-white/10 rounded-full overflow-hidden">
        <div className="h-full rounded-full transition-all duration-700" style={{ width: `${pct}%`, background: color }} />
      </div>
      <span className="text-xs text-gray-500 w-6 text-right">{value}</span>
    </div>
  );
}

// ─── Stat Card ─────────────────────────────────────────────────────────────────
function StatCard({ icon: Icon, label, value, sub, color = 'text-red-400', bg = 'bg-red-500/10', trend }) {
  return (
    <div className="glass-panel p-5 flex flex-col gap-3">
      <div className="flex items-start justify-between">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${bg}`}>
          <Icon size={18} className={color} />
        </div>
        {trend !== undefined && (
          <span className={`text-xs flex items-center gap-0.5 ${trend >= 0 ? 'text-green-400' : 'text-red-400'}`}>
            {trend >= 0 ? <TrendingUp size={10} /> : <TrendingDown size={10} />}
            {Math.abs(trend)}%
          </span>
        )}
      </div>
      <div>
        <p className={`text-2xl font-bold ${color}`}>{value}</p>
        <p className="text-gray-400 text-sm font-medium mt-0.5">{label}</p>
        {sub && <p className="text-gray-600 text-xs mt-0.5">{sub}</p>}
      </div>
    </div>
  );
}

// ─── OkBot Chat Tab ────────────────────────────────────────────────────────────
const OkBotChatTab = ({ business }) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [conversationId, setConversationId] = useState(null);
  const endRef = useRef(null);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (messages.length === 0) {
      setMessages([{ role: 'assistant', content: `¡Hola ${business?.name || ''}! Soy OkBot, tu asistente de IA empresarial. ¿En qué puedo ayudarte hoy?` }]);
    }
  }, []);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const send = async () => {
    if (!input.trim() || loading) return;
    const msg = input.trim(); setInput('');
    setMessages(p => [...p, { role: 'user', content: msg }]);
    setLoading(true);
    try {
      const res = await axios.post(`${API}/chat/send`, {
        message: msg,
        conversation_id: conversationId,
        agent_type: 'general',
        user_id: business?.id || 'business'
      });
      setConversationId(res.data.conversation_id);
      setMessages(p => [...p, { role: 'assistant', content: res.data.response }]);
    } catch { setMessages(p => [...p, { role: 'assistant', content: 'Error al procesar tu mensaje. Por favor intenta de nuevo.' }]); }
    finally { setLoading(false); }
  };

  const PROMPTS = ['¿Cómo puedo mejorar mis ventas?', 'Redacta un mensaje de bienvenida', 'Analiza mi estrategia de soporte', 'Dame ideas de automatización'];

  return (
    <div className="glass-panel flex flex-col" style={{ height: 'clamp(480px, 75vh, 680px)' }}>
      <div className="flex items-center gap-3 p-3 sm:p-4 border-b border-white/10">
        <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-gradient-to-br from-red-500 to-orange-500 flex items-center justify-center flex-shrink-0">
          <img src="/okbot_logo_2026.png" alt="OkBot" style={{ width: 20, height: 20, objectFit: 'contain', filter: 'brightness(0) invert(1)' }} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-white font-semibold text-sm">OkBot IA Empresarial</p>
          <p className="text-green-400 text-xs flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-green-400 inline-block animate-pulse"></span>En línea</p>
        </div>
        <button onClick={() => { setMessages([]); setConversationId(null); }} className="p-2 text-gray-500 hover:text-white hover:bg-white/10 rounded-lg transition-colors">
          <RotateCcw size={14} />
        </button>
      </div>
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {messages.map((msg, i) => (
          <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            {msg.role === 'assistant' && (
              <div className="w-7 h-7 rounded-full bg-gradient-to-br from-red-500 to-orange-500 flex items-center justify-center mr-2 flex-shrink-0 mt-1">
                <img src="/okbot_logo_2026.png" alt="OkBot" style={{ width: 14, height: 14, objectFit: 'contain', filter: 'brightness(0) invert(1)' }} />
              </div>
            )}
            <div className={`max-w-[75%] p-3 rounded-2xl text-sm ${msg.role === 'user' ? 'bg-red-500 text-white rounded-br-md' : 'bg-white/10 text-white rounded-bl-md'}`}>
              <p className="whitespace-pre-wrap">{msg.content}</p>
            </div>
          </div>
        ))}
        {loading && (
          <div className="flex justify-start">
            <div className="w-7 h-7 rounded-full bg-gradient-to-br from-red-500 to-orange-500 flex items-center justify-center mr-2 flex-shrink-0">
              <img src="/okbot_logo_2026.png" alt="OkBot" style={{ width: 14, height: 14, objectFit: 'contain', filter: 'brightness(0) invert(1)' }} />
            </div>
            <div className="bg-white/10 p-3 rounded-2xl rounded-bl-md flex gap-1">
              {[0,150,300].map(d => <span key={d} className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: `${d}ms` }} />)}
            </div>
          </div>
        )}
        <div ref={endRef} />
      </div>
      {messages.length <= 1 && (
        <div className="px-3 sm:px-4 pb-2 grid grid-cols-1 sm:grid-cols-2 gap-2">
          {PROMPTS.map((p, i) => (
            <button key={i} onClick={() => { setInput(p); }} className="text-left px-3 py-2 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl text-xs text-gray-400 hover:text-white transition-colors">
              {p}
            </button>
          ))}
        </div>
      )}
      <div className="p-4 border-t border-white/10">
        <div className="flex gap-2">
          <input type="text" value={input} onChange={e => setInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && send()}
            placeholder="Escribe un mensaje a OkBot..." disabled={loading}
            className="flex-1 bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-white placeholder-gray-600 focus:outline-none focus:border-red-400 text-sm" />
          <button onClick={send} disabled={loading || !input.trim()} className="p-2.5 bg-red-500 hover:bg-red-600 disabled:opacity-40 rounded-xl transition-colors">
            <Send size={16} className="text-white" />
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── CHATS TAB ────────────────────────────────────────────────────────────────
const ChatsTab = ({ business, onSimulate }) => {
  const [chats, setChats] = useState([]);
  const [selected, setSelected] = useState(null);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [reply, setReply] = useState('');
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState('chats'); // chats | simulator
  const [simMessages, setSimMessages] = useState([]);
  const [simInput, setSimInput] = useState('');
  const [simLoading, setSimLoading] = useState(false);
  const [simMode, setSimMode] = useState('client'); // client | bot
  const [annotations, setAnnotations] = useState({});
  const endRef = useRef(null);
  const simEndRef = useRef(null);

  const load = useCallback(async () => {
    try { const r = await axios.get(`${API}/business/${business.id}/chats`); setChats(r.data || []); } catch {}
  }, [business.id]);

  useEffect(() => { load(); const t = setInterval(load, 15000); return () => clearInterval(t); }, [load]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [selected?.messages]);
  useEffect(() => { simEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [simMessages]);

  const filters = [
    { id: 'all', label: 'Todos' },
    { id: 'bot', label: 'Bot activo' },
    { id: 'manual', label: 'Control manual' },
  ];

  const filtered = chats.filter(c => {
    const matchF = filter === 'all' || (filter === 'bot' && !c.manual_control) || (filter === 'manual' && c.manual_control);
    const matchS = !search || c.user_id?.includes(search);
    return matchF && matchS;
  });

  const sendReply = async () => {
    if (!reply.trim() || !selected) return;
    setLoading(true);
    try {
      await axios.post(`${API}/business/${business.id}/chats/${selected.id}/respond`, null, { params: { message: reply } });
      setReply(''); toast.success('Mensaje enviado'); load();
    } catch { toast.error('Error al enviar'); } finally { setLoading(false); }
  };

  const takeControl = async (chatId) => {
    try { await axios.post(`${API}/business/${business.id}/chats/${chatId}/take-control`); toast.success('Control manual activado'); load(); } catch { toast.error('Error'); }
  };
  const returnToBot = async (chatId) => {
    try { await axios.post(`${API}/business/${business.id}/chats/${chatId}/return-to-bot`); toast.success('Bot reactivado'); load(); } catch { toast.error('Error'); }
  };

  const startSim = async () => {
    setSimMessages([{ role: 'bot', content: '¡Hola! Soy el asistente virtual. ¿En qué puedo ayudarte hoy?', ts: new Date() }]);
    setView('simulator');
  };

  const sendSim = async () => {
    if (!simInput.trim() || simLoading) return;
    const msg = simInput.trim(); setSimInput('');
    setSimMessages(p => [...p, { role: simMode, content: msg, ts: new Date() }]);
    if (simMode === 'client') {
      setSimLoading(true);
      try {
        const r = await axios.post(`${API}/chat`, { message: msg });
        setSimMessages(p => [...p, { role: 'bot', content: r.data.response, ts: new Date() }]);
      } catch {
        setSimMessages(p => [...p, { role: 'bot', content: 'Error al procesar.', ts: new Date() }]);
      } finally { setSimLoading(false); }
    }
  };

  const QUICK_REPLIES = ['Gracias por contactarnos', '¿Puedes darnos más detalles?', 'Nuestro equipo te contactará pronto', 'Por favor espera un momento'];

  return (
    <div className="space-y-4">
      {/* Header Bar */}
      <div className="flex items-center justify-between">
        <div className="flex gap-1 p-1 bg-white/5 rounded-xl">
          <button onClick={() => setView('chats')} className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${view !== 'simulator' ? 'bg-red-500 text-white' : 'text-gray-400 hover:text-white'}`}>
            <MessageSquare size={14} />Conversaciones
          </button>
          <button onClick={startSim} className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${view === 'simulator' ? 'bg-red-500 text-white' : 'text-gray-400 hover:text-white'}`}>
            <TestTube size={14} />Simulador
          </button>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={load} className="p-2 text-gray-400 hover:text-white hover:bg-white/10 rounded-lg"><RefreshCw size={14} /></button>
          <button onClick={() => { axios.post(`${API}/business/${business.id}/simulate-inquiry`).then(() => { toast.success('Consulta de prueba creada'); load(); }); }} className="px-2.5 py-1.5 bg-white/10 hover:bg-white/15 text-gray-300 text-xs sm:text-sm rounded-lg flex items-center gap-1.5 transition-colors">
            <Zap size={13} /><span className="hidden xs:inline sm:inline">Simular consulta</span>
          </button>
        </div>
      </div>

      {view === 'simulator' ? (
        /* ── SIMULATOR ── */
        <div className="glass-panel overflow-hidden flex flex-col" style={{ height: 'clamp(480px, 78vh, 640px)' }}>
          <div className="flex items-center gap-2 sm:gap-3 p-3 sm:p-4 border-b border-white/10 bg-gradient-to-r from-purple-500/10 to-blue-500/10 flex-wrap">
            <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-xl bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center flex-shrink-0">
              <TestTube size={16} className="text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-white font-semibold text-sm">Simulador de Conversaciones</p>
              <p className="text-gray-400 text-xs hidden sm:block">Prueba el bot en tiempo real con IA</p>
            </div>
            <div className="flex items-center gap-1 bg-white/5 border border-white/10 rounded-xl p-1 flex-shrink-0">
              <button onClick={() => setSimMode('client')} className={`px-2 sm:px-3 py-1 rounded-lg text-xs font-medium transition-colors ${simMode === 'client' ? 'bg-blue-500 text-white' : 'text-gray-400 hover:text-white'}`}>
                <User size={10} className="inline mr-0.5" />Cliente
              </button>
              <button onClick={() => setSimMode('business')} className={`px-2 sm:px-3 py-1 rounded-lg text-xs font-medium transition-colors ${simMode === 'business' ? 'bg-green-500 text-white' : 'text-gray-400 hover:text-white'}`}>
                <Bot size={10} className="inline mr-0.5" />Negocio
              </button>
            </div>
            <button onClick={() => { setSimMessages([]); }} className="p-1.5 text-gray-500 hover:text-white hover:bg-white/10 rounded-lg flex-shrink-0"><RotateCcw size={13} /></button>
            <button onClick={() => setView('chats')} className="p-1.5 text-gray-500 hover:text-white hover:bg-white/10 rounded-lg flex-shrink-0"><X size={16} /></button>
          </div>
          <div className="flex-1 overflow-y-auto p-4 space-y-3" style={{ minHeight: 0 }}>
            {simMessages.length === 0 && (
              <div className="text-center py-10">
                <TestTube size={32} className="text-gray-600 mx-auto mb-3" />
                <p className="text-gray-500 text-sm">Escribe un mensaje para iniciar la simulación</p>
                <div className="flex flex-wrap gap-2 justify-center mt-4">
                  {['Hola, necesito ayuda', 'Quiero más información', '¿Cuánto cuesta?', 'Tengo un problema'].map(s => (
                    <button key={s} onClick={() => setSimInput(s)} className="px-3 py-1.5 bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg text-xs text-gray-400 hover:text-white transition-colors">{s}</button>
                  ))}
                </div>
              </div>
            )}
            {simMessages.map((m, i) => (
              <div key={i} className={`flex ${m.role === 'client' ? 'justify-end' : 'justify-start'}`}>
                {m.role === 'bot' && (
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-red-500 to-orange-400 flex items-center justify-center mr-2 flex-shrink-0 mt-0.5">
                    <Bot size={14} className="text-white" />
                  </div>
                )}
                {m.role === 'business' && (
                  <div className="w-8 h-8 rounded-full bg-green-500/20 border border-green-500/30 flex items-center justify-center mr-2 flex-shrink-0 mt-0.5">
                    <User size={14} className="text-green-400" />
                  </div>
                )}
                <div className="max-w-[70%]">
                  <div className={`p-3 rounded-2xl text-sm ${
                    m.role === 'client' ? 'bg-blue-500 text-white rounded-br-md'
                    : m.role === 'business' ? 'bg-green-500/20 text-green-100 rounded-bl-md border border-green-500/20'
                    : 'bg-white/10 text-white rounded-bl-md'
                  }`}>
                    <p className="whitespace-pre-wrap">{m.content}</p>
                  </div>
                  <p className="text-gray-600 text-xs mt-1 px-1">{m.ts?.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' })}</p>
                </div>
                {m.role === 'client' && (
                  <div className="w-8 h-8 rounded-full bg-blue-500/20 border border-blue-500/30 flex items-center justify-center ml-2 flex-shrink-0 mt-0.5">
                    <User size={14} className="text-blue-400" />
                  </div>
                )}
              </div>
            ))}
            {simLoading && (
              <div className="flex justify-start">
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-red-500 to-orange-400 flex items-center justify-center mr-2 flex-shrink-0">
                  <Bot size={14} className="text-white" />
                </div>
                <div className="bg-white/10 p-3 rounded-2xl rounded-bl-md flex gap-1">
                  {[0,150,300].map(d => <span key={d} className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: `${d}ms` }} />)}
                </div>
              </div>
            )}
            <div ref={simEndRef} />
          </div>
          <div className="p-3 border-t border-white/10">
            <div className="flex gap-2">
              <input value={simInput} onChange={e => setSimInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && sendSim()}
                placeholder={`Mensaje como ${simMode === 'client' ? 'cliente' : 'negocio'}...`}
                className="flex-1 bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-white placeholder-gray-600 text-sm focus:outline-none focus:border-purple-400" />
              <button onClick={sendSim} disabled={simLoading || !simInput.trim()} className="px-4 py-2 bg-purple-500 hover:bg-purple-600 disabled:opacity-40 rounded-xl text-white text-sm font-medium transition-colors flex items-center gap-2">
                <Send size={14} />Enviar
              </button>
            </div>
          </div>
        </div>
      ) : (
        /* ── CONVERSATION LIST + DETAIL ── */
        <div className="flex flex-col lg:grid lg:grid-cols-5 gap-4" style={{ minHeight: 'clamp(480px, 78vh, 640px)' }}>
          {/* Left: List */}
          <div className="col-span-2 flex flex-col gap-3 lg:max-h-[620px]">
            <div className="relative">
              <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
              <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Buscar conversaciones..."
                className="w-full pl-9 pr-3 py-2 bg-white/5 border border-white/10 rounded-xl text-sm text-white placeholder-gray-600 focus:outline-none focus:border-red-400" />
            </div>
            <div className="flex gap-1">
              {filters.map(f => (
                <button key={f.id} onClick={() => setFilter(f.id)} className={`flex-1 py-1 rounded-lg text-xs font-medium transition-colors ${filter === f.id ? 'bg-red-500 text-white' : 'bg-white/5 text-gray-400 hover:bg-white/10'}`}>{f.label}</button>
              ))}
            </div>
            <div className="flex-1 overflow-y-auto space-y-2 pr-1">
              {filtered.length === 0 ? (
                <div className="text-center py-12"><MessageSquare size={28} className="text-gray-600 mx-auto mb-2" /><p className="text-gray-500 text-sm">Sin conversaciones</p></div>
              ) : filtered.map(chat => (
                <button key={chat.id} onClick={() => setSelected(chat)} className={`w-full glass-panel p-3 text-left transition-all hover:bg-white/5 ${selected?.id === chat.id ? 'ring-1 ring-red-500/50 bg-red-500/5' : ''}`}>
                  <div className="flex items-center gap-2.5 mb-1.5">
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                      {avatar(chat.user_id)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <p className="text-white text-xs font-medium truncate">#{chat.user_id?.slice(0, 12)}</p>
                        <p className="text-gray-600 text-xs flex-shrink-0 ml-1">{fmtTime(chat.updated_at)}</p>
                      </div>
                      <div className="flex items-center gap-1.5 mt-0.5">
                        <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${chat.manual_control ? 'bg-orange-500/20 text-orange-400' : 'bg-green-500/20 text-green-400'}`}>
                          {chat.manual_control ? 'Manual' : 'Bot'}
                        </span>
                        <span className="text-gray-600 text-xs">{chat.messages?.length || 0} msgs</span>
                      </div>
                    </div>
                  </div>
                  <p className="text-gray-500 text-xs truncate pl-10">{chat.messages?.[chat.messages.length - 1]?.content || 'Sin mensajes'}</p>
                </button>
              ))}
            </div>
          </div>

          {/* Right: Detail */}
          <div className="col-span-3 glass-panel flex flex-col overflow-hidden" style={{ minHeight: 'clamp(320px, 55vh, 620px)' }}>
            {selected ? (
              <>
                <div className="flex items-center justify-between p-4 border-b border-white/10 flex-shrink-0">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center text-white font-bold">
                      {avatar(selected.user_id)}
                    </div>
                    <div>
                      <p className="text-white font-medium text-sm">Usuario #{selected.user_id?.slice(0, 12)}</p>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className={`text-xs px-2 py-0.5 rounded-full border ${selected.manual_control ? 'bg-orange-500/15 border-orange-500/30 text-orange-400' : 'bg-green-500/15 border-green-500/30 text-green-400'}`}>
                          {selected.manual_control ? '👤 Control manual' : '🤖 Bot activo'}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {selected.manual_control ? (
                      <button onClick={() => returnToBot(selected.id)} className="px-3 py-1.5 bg-green-500/15 hover:bg-green-500/25 text-green-400 border border-green-500/30 rounded-lg text-xs font-medium transition-colors flex items-center gap-1.5">
                        <Bot size={12} />Devolver al Bot
                      </button>
                    ) : (
                      <button onClick={() => takeControl(selected.id)} className="px-3 py-1.5 bg-orange-500/15 hover:bg-orange-500/25 text-orange-400 border border-orange-500/30 rounded-lg text-xs font-medium transition-colors flex items-center gap-1.5">
                        <User size={12} />Tomar Control
                      </button>
                    )}
                    <button onClick={() => setSelected(null)} className="p-1.5 text-gray-500 hover:text-white hover:bg-white/10 rounded-lg"><X size={14} /></button>
                  </div>
                </div>
                <div className="flex-1 overflow-y-auto p-4 space-y-3">
                  {(selected.messages || []).map((msg, i) => (
                    <div key={i} className={`flex ${msg.from === 'user' ? 'justify-start' : 'justify-end'}`}>
                      <div className={`max-w-[72%] p-3 rounded-2xl text-sm ${
                        msg.from === 'user' ? 'bg-white/10 text-white rounded-bl-md'
                        : msg.from === 'business' ? 'bg-blue-500/20 text-blue-100 rounded-br-md border border-blue-500/20'
                        : 'bg-green-500/20 text-green-100 rounded-br-md'
                      }`}>
                        <p className="whitespace-pre-wrap">{msg.content}</p>
                        <p className="text-xs opacity-50 mt-1">
                          {msg.from === 'bot' ? '🤖 Bot' : msg.from === 'business' ? '👤 Tú' : '👤 Usuario'} · {fmtTime(msg.timestamp)}
                        </p>
                      </div>
                    </div>
                  ))}
                  <div ref={endRef} />
                </div>
                <div className="p-3 border-t border-white/10 space-y-2 flex-shrink-0">
                  {selected.manual_control && (
                    <>
                      <div className="flex gap-1 flex-wrap">
                        {QUICK_REPLIES.map((r, i) => (
                          <button key={i} onClick={() => setReply(r)} className="px-2 py-1 bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg text-xs text-gray-400 hover:text-white transition-colors">{r}</button>
                        ))}
                      </div>
                      <div className="flex gap-2">
                        <input value={reply} onChange={e => setReply(e.target.value)} onKeyDown={e => e.key === 'Enter' && sendReply()}
                          placeholder="Escribe tu respuesta..." className="flex-1 bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-white placeholder-gray-600 text-sm focus:outline-none focus:border-red-400" />
                        <button onClick={sendReply} disabled={loading || !reply.trim()} className="p-2 bg-red-500 hover:bg-red-600 disabled:opacity-40 rounded-xl transition-colors">
                          <Send size={16} className="text-white" />
                        </button>
                      </div>
                    </>
                  )}
                  {!selected.manual_control && (
                    <p className="text-gray-600 text-xs text-center py-1">El bot está respondiendo automáticamente. Toma el control para responder manualmente.</p>
                  )}
                </div>
              </>
            ) : (
              <div className="flex-1 flex flex-col items-center justify-center text-center p-8">
                <div className="w-16 h-16 rounded-2xl bg-white/5 flex items-center justify-center mb-4">
                  <MessageSquare size={28} className="text-gray-600" />
                </div>
                <p className="text-gray-400 font-medium">Selecciona una conversación</p>
                <p className="text-gray-600 text-sm mt-1">Ver mensajes, tomar control o devolver al bot</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// ─── ANALYTICS TAB ────────────────────────────────────────────────────────────
const AnalyticsTab = ({ business }) => {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('7d');

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try { const r = await axios.get(`${API}/business/${business.id}/analytics`); setAnalytics(r.data); }
      catch {}
      finally { setLoading(false); }
    };
    load();
  }, [business.id, period]);

  if (loading) return <div className="flex items-center justify-center h-64"><RefreshCw size={24} className="text-red-400 animate-spin" /></div>;
  if (!analytics) return <div className="text-center py-20 text-gray-500">Sin datos de analytics</div>;

  const days = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];
  const dailyData = analytics.daily_conversations || days.map((d, i) => ({ day: d, count: Math.floor(Math.random() * 20 + 5) }));
  const maxDaily = Math.max(...dailyData.map(d => d.count || d));
  const topics = analytics.top_topics || ['Información general', 'Precios', 'Soporte técnico', 'Facturación', 'Cancelaciones'];

  return (
    <div className="space-y-5">
      {/* Period Selector */}
      <div className="flex items-center justify-between">
        <h3 className="text-white font-semibold">Panel de Analíticas</h3>
        <div className="flex gap-1 p-1 bg-white/5 rounded-xl">
          {['7d', '30d', '90d'].map(p => (
            <button key={p} onClick={() => setPeriod(p)} className={`px-3 py-1 rounded-lg text-xs font-medium transition-colors ${period === p ? 'bg-red-500 text-white' : 'text-gray-400 hover:text-white'}`}>{p}</button>
          ))}
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 xl:grid-cols-4 gap-3 sm:gap-4">
        <StatCard icon={MessageSquare} label="Total Conversaciones" value={analytics.total_conversations || 0} color="text-blue-400" bg="bg-blue-500/10" trend={12} />
        <StatCard icon={Users} label="Solicitudes Activas" value={analytics.active_requests || 0} color="text-green-400" bg="bg-green-500/10" trend={-3} />
        <StatCard icon={TrendingUp} label="Tasa de Respuesta" value={`${analytics.response_rate || 98}%`} color="text-purple-400" bg="bg-purple-500/10" trend={5} />
        <StatCard icon={Clock} label="Tiempo Promedio" value={`${analytics.avg_response_time_minutes || 2}m`} color="text-orange-400" bg="bg-orange-500/10" trend={-8} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-5">
        {/* Bar Chart */}
        <div className="col-span-1 lg:col-span-2 glass-panel p-4 sm:p-5">
          <div className="flex items-center justify-between mb-5">
            <div>
              <h4 className="text-white font-semibold text-sm">Conversaciones por día</h4>
              <p className="text-gray-500 text-xs mt-0.5">Últimos 7 días</p>
            </div>
            <BarChart2 size={18} className="text-gray-500" />
          </div>
          <div className="flex items-end gap-2 h-32">
            {(Array.isArray(dailyData) ? dailyData : days.map((d, i) => ({ day: d, count: 5 + i * 2 }))).map((item, i) => {
              const count = typeof item === 'object' ? (item.count || 0) : item;
              const day = typeof item === 'object' ? (item.day || days[i]) : days[i];
              const h = maxDaily > 0 ? (count / maxDaily) * 100 : 20;
              return (
                <div key={i} className="flex-1 flex flex-col items-center gap-1.5 group">
                  <span className="text-xs text-gray-500 opacity-0 group-hover:opacity-100 transition-opacity">{count}</span>
                  <div className="w-full rounded-t-md transition-all duration-700 group-hover:opacity-90" style={{ height: `${h}%`, background: `linear-gradient(to top, #ef4444, #f97316)` }} />
                  <span className="text-xs text-gray-600">{day}</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Satisfaction */}
        <div className="glass-panel p-5">
          <div className="flex items-center justify-between mb-5">
            <div>
              <h4 className="text-white font-semibold text-sm">Satisfacción</h4>
              <p className="text-gray-500 text-xs mt-0.5">Promedio general</p>
            </div>
            <Star size={18} className="text-yellow-400" />
          </div>
          <div className="text-center mb-4">
            <p className="text-5xl font-bold text-white">{analytics.satisfaction_score || 4.7}</p>
            <div className="flex justify-center gap-1 mt-2">
              {[1,2,3,4,5].map(s => (
                <Star key={s} size={16} fill={s <= Math.round(analytics.satisfaction_score || 4.7) ? '#f59e0b' : 'none'} className={s <= Math.round(analytics.satisfaction_score || 4.7) ? 'text-yellow-400' : 'text-gray-600'} />
              ))}
            </div>
            <p className="text-gray-500 text-xs mt-2">de 5 estrellas</p>
          </div>
          <div className="space-y-2">
            {[5,4,3,2,1].map(s => (
              <div key={s} className="flex items-center gap-2">
                <span className="text-xs text-gray-500 w-3">{s}</span>
                <div className="flex-1 h-1.5 bg-white/10 rounded-full overflow-hidden">
                  <div className="h-full rounded-full bg-yellow-400 transition-all duration-700"
                    style={{ width: `${s === 5 ? 60 : s === 4 ? 25 : s === 3 ? 10 : s === 2 ? 3 : 2}%` }} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-5">
        {/* Top Topics */}
        <div className="glass-panel p-4 sm:p-5">
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-white font-semibold text-sm">Temas Principales</h4>
            <Hash size={16} className="text-gray-500" />
          </div>
          <div className="space-y-3">
            {topics.slice(0, 6).map((topic, i) => {
              const t = typeof topic === 'string' ? topic : topic.name;
              const count = typeof topic === 'object' ? topic.count : (20 - i * 3);
              const max = typeof topics[0] === 'object' ? topics[0].count : 20;
              return (
                <div key={i}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-gray-300 text-xs">{t}</span>
                    <span className="text-gray-500 text-xs">{count}</span>
                  </div>
                  <MiniBar value={count} max={max} color={COLORS[i % COLORS.length]} />
                </div>
              );
            })}
          </div>
        </div>

        {/* Bot vs Manual */}
        <div className="glass-panel p-5">
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-white font-semibold text-sm">Bot vs Manual</h4>
            <PieChart size={16} className="text-gray-500" />
          </div>
          <div className="space-y-4">
            {[
              { label: 'Respondidas por Bot', value: analytics.bot_handled || 85, color: '#10b981', icon: Bot },
              { label: 'Control Manual', value: analytics.manual_handled || 12, color: '#f59e0b', icon: User },
              { label: 'Sin respuesta', value: analytics.unhandled || 3, color: '#ef4444', icon: AlertCircle },
            ].map((item, i) => {
              const Icon = item.icon;
              return (
                <div key={i} className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: `${item.color}20` }}>
                    <Icon size={14} style={{ color: item.color }} />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-gray-300 text-xs">{item.label}</span>
                      <span className="text-white text-xs font-semibold">{item.value}%</span>
                    </div>
                    <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
                      <div className="h-full rounded-full" style={{ width: `${item.value}%`, background: item.color }} />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="mt-5 pt-4 border-t border-white/10">
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-white/5 rounded-xl p-3 text-center">
                <p className="text-white font-bold text-lg">{analytics.peak_hour || '10am'}</p>
                <p className="text-gray-500 text-xs">Hora pico</p>
              </div>
              <div className="bg-white/5 rounded-xl p-3 text-center">
                <p className="text-white font-bold text-lg">{analytics.avg_messages_per_chat || 6}</p>
                <p className="text-gray-500 text-xs">Msgs por chat</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── SCHEDULE TAB ─────────────────────────────────────────────────────────────
const ScheduleTab = ({ business }) => {
  const [schedule, setSchedule] = useState(null);
  const [automations, setAutomations] = useState([]);
  const [newAuto, setNewAuto] = useState({ name: '', trigger: '', action: '', type: 'keyword' });
  const [showAddAuto, setShowAddAuto] = useState(false);
  const [loading, setLoading] = useState(true);

  const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
  const dayLabels = { monday: 'Lunes', tuesday: 'Martes', wednesday: 'Miércoles', thursday: 'Jueves', friday: 'Viernes', saturday: 'Sábado', sunday: 'Domingo' };

  useEffect(() => {
    const load = async () => {
      try {
        const [schedRes, autoRes] = await Promise.all([
          axios.get(`${API}/business/${business.id}/schedule`),
          axios.get(`${API}/business/${business.id}/automations`).catch(() => ({ data: { automations: [] } })),
        ]);
        setSchedule(schedRes.data);
        setAutomations(autoRes.data.automations || []);
      } catch {
        setSchedule(Object.fromEntries(days.map(d => [d, { active: d !== 'saturday' && d !== 'sunday', start: '09:00', end: '18:00' }])));
      } finally { setLoading(false); }
    };
    load();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [business.id]);

  const saveAutomations = async (list) => {
    try { await axios.put(`${API}/business/${business.id}/automations`, { automations: list }); }
    catch { toast.error('Error al guardar automatizaciones'); }
  };

  const updateSchedule = async (day, field, value) => {
    const ns = { ...schedule, [day]: { ...schedule[day], [field]: value } };
    setSchedule(ns);
    try { await axios.put(`${API}/business/${business.id}/schedule`, ns); toast.success('Horario guardado'); } catch { toast.error('Error'); }
  };

  const toggleAuto = async (id) => {
    const updated = automations.map(a => a.id === id ? { ...a, active: !a.active } : a);
    setAutomations(updated);
    await saveAutomations(updated);
  };
  const deleteAuto = async (id) => {
    const updated = automations.filter(a => a.id !== id);
    setAutomations(updated);
    await saveAutomations(updated);
    toast.success('Automatización eliminada');
  };
  const addAuto = async () => {
    if (!newAuto.name || !newAuto.trigger) { toast.error('Completa los campos'); return; }
    const updated = [...automations, { ...newAuto, id: String(Date.now()), active: true }];
    setAutomations(updated);
    await saveAutomations(updated);
    setNewAuto({ name: '', trigger: '', action: '', type: 'keyword' });
    setShowAddAuto(false);
    toast.success('Automatización guardada');
  };

  const AUTO_ICONS = { keyword: Hash, schedule: Clock, escalation: AlertTriangle, resolution: CheckCircle };
  const AUTO_COLORS = { keyword: 'text-blue-400 bg-blue-500/10', schedule: 'text-purple-400 bg-purple-500/10', escalation: 'text-orange-400 bg-orange-500/10', resolution: 'text-green-400 bg-green-500/10' };

  if (loading) return <div className="flex items-center justify-center h-64"><RefreshCw size={24} className="text-red-400 animate-spin" /></div>;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
      {/* Schedule */}
      <div className="glass-panel p-4 sm:p-5">
        <div className="flex items-center gap-2 mb-5">
          <Clock size={18} className="text-red-400" />
          <h3 className="text-white font-semibold">Horario de Atención</h3>
        </div>
        <p className="text-gray-500 text-xs mb-4">El bot responderá automáticamente en los horarios activos</p>
        <div className="space-y-3">
          {schedule && days.map(day => {
            const d = schedule[day] || { active: true, start: '09:00', end: '18:00' };
            return (
              <div key={day} className={`p-3 rounded-xl border transition-colors ${d.active !== false ? 'border-white/10 bg-white/3' : 'border-white/5 bg-white/1 opacity-50'}`}>
                <div className="flex items-center gap-3">
                  <button onClick={() => updateSchedule(day, 'active', !d.active)} className="flex-shrink-0">
                    {d.active !== false
                      ? <ToggleRight size={22} className="text-green-400" />
                      : <ToggleLeft size={22} className="text-gray-600" />}
                  </button>
                  <span className="text-white text-sm font-medium w-20">{dayLabels[day]}</span>
                  <div className="flex items-center gap-2 flex-1">
                    <input type="time" value={d.start || '09:00'} onChange={e => updateSchedule(day, 'start', e.target.value)}
                      disabled={d.active === false}
                      className="bg-black/30 border border-white/10 rounded-lg px-2 py-1 text-white text-xs focus:outline-none focus:border-red-400 disabled:opacity-30" />
                    <span className="text-gray-600 text-xs">—</span>
                    <input type="time" value={d.end || '18:00'} onChange={e => updateSchedule(day, 'end', e.target.value)}
                      disabled={d.active === false}
                      className="bg-black/30 border border-white/10 rounded-lg px-2 py-1 text-white text-xs focus:outline-none focus:border-red-400 disabled:opacity-30" />
                  </div>
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium flex-shrink-0 ${d.active !== false ? 'bg-green-500/20 text-green-400' : 'bg-gray-500/20 text-gray-400'}`}>
                    {d.active !== false ? 'ON' : 'OFF'}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Automations */}
      <div className="glass-panel p-5">
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-2">
            <Zap size={18} className="text-yellow-400" />
            <h3 className="text-white font-semibold">Automatizaciones</h3>
          </div>
          <button onClick={() => setShowAddAuto(v => !v)} className="px-3 py-1.5 bg-red-500 hover:bg-red-600 text-white text-xs rounded-lg font-medium transition-colors flex items-center gap-1.5">
            <Plus size={13} />{showAddAuto ? 'Cancelar' : 'Nueva'}
          </button>
        </div>

        {showAddAuto && (
          <div className="mb-4 p-4 bg-white/5 border border-white/10 rounded-xl space-y-3">
            <input value={newAuto.name} onChange={e => setNewAuto(p => ({ ...p, name: e.target.value }))}
              placeholder="Nombre de la automatización" className="w-full bg-black/30 border border-white/10 rounded-lg px-3 py-2 text-white text-sm placeholder-gray-600 focus:outline-none focus:border-red-400" />
            <select value={newAuto.type} onChange={e => setNewAuto(p => ({ ...p, type: e.target.value }))}
              className="w-full bg-black/30 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-red-400">
              <option value="keyword">Por palabra clave</option>
              <option value="schedule">Por horario</option>
              <option value="escalation">Escalación</option>
              <option value="resolution">Resolución automática</option>
            </select>
            <input value={newAuto.trigger} onChange={e => setNewAuto(p => ({ ...p, trigger: e.target.value }))}
              placeholder="Condición disparadora" className="w-full bg-black/30 border border-white/10 rounded-lg px-3 py-2 text-white text-sm placeholder-gray-600 focus:outline-none focus:border-red-400" />
            <input value={newAuto.action} onChange={e => setNewAuto(p => ({ ...p, action: e.target.value }))}
              placeholder="Acción a ejecutar" className="w-full bg-black/30 border border-white/10 rounded-lg px-3 py-2 text-white text-sm placeholder-gray-600 focus:outline-none focus:border-red-400" />
            <button onClick={addAuto} className="w-full py-2 bg-red-500 hover:bg-red-600 text-white text-sm font-medium rounded-lg transition-colors">Crear Automatización</button>
          </div>
        )}

        <div className="space-y-3">
          {automations.map(auto => {
            const Icon = AUTO_ICONS[auto.type] || Zap;
            const colorClass = AUTO_COLORS[auto.type] || 'text-gray-400 bg-gray-500/10';
            return (
              <div key={auto.id} className={`p-3.5 rounded-xl border transition-all ${auto.active ? 'border-white/10 bg-white/3' : 'border-white/5 bg-white/1 opacity-50'}`}>
                <div className="flex items-start gap-3">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${colorClass}`}>
                    <Icon size={14} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-white text-sm font-medium">{auto.name}</p>
                    <p className="text-gray-500 text-xs mt-0.5">Si: {auto.trigger}</p>
                    {auto.action && <p className="text-gray-600 text-xs">Entonces: {auto.action}</p>}
                  </div>
                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    <button onClick={() => toggleAuto(auto.id)}>
                      {auto.active ? <ToggleRight size={20} className="text-green-400" /> : <ToggleLeft size={20} className="text-gray-600" />}
                    </button>
                    <button onClick={() => deleteAuto(auto.id)} className="p-1 text-gray-600 hover:text-red-400 transition-colors">
                      <Trash2 size={13} />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

// ─── SUPPORT TAB ──────────────────────────────────────────────────────────────
const SupportTab = ({ business }) => {
  const [view, setView] = useState('kanban'); // kanban | list | new
  const [tickets, setTickets] = useState([]);
  const [selected, setSelected] = useState(null);
  const [newTicket, setNewTicket] = useState({ subject: '', topic: 'otro', description: '', priority: 'normal' });
  const [loading, setLoading] = useState(false);
  const [slaRules, setSlaRules] = useState([
    { priority: 'urgent', hours: 1, label: 'Urgente', color: 'text-red-400' },
    { priority: 'high', hours: 4, label: 'Alta', color: 'text-orange-400' },
    { priority: 'normal', hours: 24, label: 'Normal', color: 'text-blue-400' },
    { priority: 'low', hours: 72, label: 'Baja', color: 'text-gray-400' },
  ]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadTickets(); }, [business.id]);

  const loadTickets = async () => {
    try { const r = await axios.get(`${API}/tickets/business/${business.id}`); setTickets(r.data || []); } catch {}
  };

  const createTicket = async () => {
    if (!newTicket.subject || !newTicket.description) { toast.error('Completa todos los campos'); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/tickets?user_id=${business.id}&user_type=business`, { ...newTicket, category: newTicket.topic, user_type: 'business' });
      toast.success('Ticket creado'); setView('kanban'); setNewTicket({ subject: '', topic: 'otro', description: '', priority: 'normal' }); loadTickets();
    } catch { toast.error('Error'); } finally { setLoading(false); }
  };

  const TOPICS = ['cuenta', 'facturacion', 'bot', 'analytics', 'integraciones', 'api', 'bug', 'sugerencia', 'otro'];
  const COLUMNS = [
    { id: 'open', label: 'Abiertos', color: 'text-yellow-400', border: 'border-yellow-500/30' },
    { id: 'in_progress', label: 'En Progreso', color: 'text-blue-400', border: 'border-blue-500/30' },
    { id: 'resolved', label: 'Resueltos', color: 'text-green-400', border: 'border-green-500/30' },
  ];

  const byCols = (col) => tickets.filter(t => {
    if (col === 'open') return t.status === 'open';
    if (col === 'in_progress') return ['in_progress', 'waiting_user'].includes(t.status);
    return ['resolved', 'closed'].includes(t.status);
  });

  const TicketCard = ({ ticket, compact }) => {
    const pri = PRIORITY_CONFIG[ticket.priority] || PRIORITY_CONFIG.normal;
    const sta = STATUS_CONFIG[ticket.status] || STATUS_CONFIG.open;
    return (
      <div onClick={() => setSelected(ticket)} className={`p-3 rounded-xl border cursor-pointer hover:bg-white/5 transition-all ${selected?.id === ticket.id ? 'bg-white/5 border-red-500/30' : 'border-white/10 bg-white/2'}`}>
        <div className="flex items-start justify-between gap-2 mb-2">
          <p className="text-white text-xs font-medium line-clamp-2">{ticket.subject}</p>
          <span className={`w-2 h-2 rounded-full flex-shrink-0 mt-1 ${pri.dot}`} />
        </div>
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className={`text-xs px-1.5 py-0.5 rounded border ${sta.bg} ${sta.color}`}>{sta.label}</span>
          <span className="text-gray-600 text-xs">{fmtTime(ticket.created_at)}</span>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex gap-1 p-1 bg-white/5 rounded-xl">
          {[{ id: 'kanban', label: 'Kanban', icon: Layers }, { id: 'list', label: 'Lista', icon: FileText }].map(v => (
            <button key={v.id} onClick={() => { setView(v.id); setSelected(null); }} className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors flex items-center gap-1.5 ${view === v.id ? 'bg-red-500 text-white' : 'text-gray-400 hover:text-white'}`}>
              <v.icon size={13} />{v.label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <span className="text-gray-500 text-xs">{tickets.length} tickets</span>
          <button onClick={() => setView('new')} className="px-3 py-1.5 bg-red-500 hover:bg-red-600 text-white text-xs rounded-lg font-medium transition-colors flex items-center gap-1.5">
            <Plus size={13} />Nuevo Ticket
          </button>
        </div>
      </div>

      {view === 'new' && (
        <div className="glass-panel p-6 space-y-4">
          <h3 className="text-white font-semibold flex items-center gap-2"><Plus size={16} className="text-red-400" />Nuevo Ticket de Soporte</h3>
          <input value={newTicket.subject} onChange={e => setNewTicket(p => ({ ...p, subject: e.target.value }))} placeholder="Asunto del ticket *"
            className="w-full bg-black/20 border border-white/10 rounded-xl px-4 py-3 text-white text-sm placeholder-gray-600 focus:outline-none focus:border-red-400" />
          <div className="grid grid-cols-2 gap-4">
            <select value={newTicket.topic} onChange={e => setNewTicket(p => ({ ...p, topic: e.target.value }))}
              className="bg-black/20 border border-white/10 rounded-xl px-3 py-3 text-white text-sm focus:outline-none focus:border-red-400">
              {TOPICS.map(t => <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>)}
            </select>
            <select value={newTicket.priority} onChange={e => setNewTicket(p => ({ ...p, priority: e.target.value }))}
              className="bg-black/20 border border-white/10 rounded-xl px-3 py-3 text-white text-sm focus:outline-none focus:border-red-400">
              {Object.entries(PRIORITY_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
          </div>
          <textarea value={newTicket.description} onChange={e => setNewTicket(p => ({ ...p, description: e.target.value }))} rows={4} placeholder="Descripción detallada *"
            className="w-full bg-black/20 border border-white/10 rounded-xl px-4 py-3 text-white text-sm placeholder-gray-600 focus:outline-none focus:border-red-400 resize-none" />
          <div className="flex gap-3">
            <button onClick={() => setView('kanban')} className="flex-1 py-2.5 bg-white/5 hover:bg-white/10 text-gray-300 rounded-xl text-sm font-medium transition-colors">Cancelar</button>
            <button onClick={createTicket} disabled={loading} className="flex-1 py-2.5 bg-red-500 hover:bg-red-600 disabled:opacity-50 text-white rounded-xl text-sm font-medium transition-colors">
              {loading ? 'Creando...' : 'Crear Ticket'}
            </button>
          </div>
        </div>
      )}

      {view === 'kanban' && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4" style={{ minHeight: 400 }}>
          {COLUMNS.map(col => {
            const colTickets = byCols(col.id);
            return (
              <div key={col.id} className={`glass-panel p-4 border-t-2 ${col.border}`}>
                <div className="flex items-center justify-between mb-3">
                  <span className={`text-sm font-semibold ${col.color}`}>{col.label}</span>
                  <span className="text-xs bg-white/10 px-2 py-0.5 rounded-full text-gray-400">{colTickets.length}</span>
                </div>
                <div className="space-y-2">
                  {colTickets.length === 0 ? (
                    <div className="text-center py-8 text-gray-600 text-xs">Sin tickets</div>
                  ) : colTickets.map(t => <TicketCard key={t.id} ticket={t} />)}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {view === 'list' && (
        <div className="space-y-3">
          {tickets.length === 0 ? (
            <div className="text-center py-20 glass-panel"><Ticket size={32} className="text-gray-600 mx-auto mb-3" /><p className="text-gray-500">Sin tickets</p></div>
          ) : tickets.map(ticket => {
            const pri = PRIORITY_CONFIG[ticket.priority] || PRIORITY_CONFIG.normal;
            const sta = STATUS_CONFIG[ticket.status] || STATUS_CONFIG.open;
            return (
              <div key={ticket.id} onClick={() => setSelected(ticket)} className={`glass-panel p-4 cursor-pointer hover:bg-white/5 transition-all flex items-center gap-4 ${selected?.id === ticket.id ? 'ring-1 ring-red-500/40' : ''}`}>
                <span className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${pri.dot}`} />
                <div className="flex-1 min-w-0">
                  <p className="text-white text-sm font-medium truncate">{ticket.subject}</p>
                  <p className="text-gray-500 text-xs mt-0.5">{ticket.topic || ticket.category} · {fmtTime(ticket.created_at)}</p>
                </div>
                <span className={`text-xs px-2 py-1 rounded-lg border flex-shrink-0 ${sta.bg} ${sta.color}`}>{sta.label}</span>
                <span className={`text-xs flex-shrink-0 ${pri.color}`}>{pri.label}</span>
              </div>
            );
          })}
        </div>
      )}

      {/* Ticket Detail Sidebar */}
      {selected && (
        <div className="glass-panel p-5 border border-white/15">
          <div className="flex items-start justify-between mb-4">
            <div>
              <h4 className="text-white font-semibold">{selected.subject}</h4>
              <div className="flex items-center gap-2 mt-1">
                {(() => { const s = STATUS_CONFIG[selected.status] || STATUS_CONFIG.open; return <span className={`text-xs px-2 py-0.5 rounded border ${s.bg} ${s.color}`}>{s.label}</span>; })()}
                {(() => { const p = PRIORITY_CONFIG[selected.priority] || PRIORITY_CONFIG.normal; return <span className={`text-xs ${p.color}`}>{p.label} prioridad</span>; })()}
              </div>
            </div>
            <button onClick={() => setSelected(null)} className="p-1.5 text-gray-500 hover:text-white hover:bg-white/10 rounded-lg"><X size={14} /></button>
          </div>
          <p className="text-gray-400 text-sm whitespace-pre-wrap mb-4">{selected.description}</p>
          {selected.messages?.length > 0 && (
            <div className="border-t border-white/10 pt-4 space-y-2">
              <p className="text-gray-500 text-xs font-medium uppercase tracking-wide">Respuestas</p>
              {selected.messages.map((m, i) => (
                <div key={i} className={`p-3 rounded-xl text-xs ${m.from === 'business' ? 'bg-blue-500/15 text-blue-100 ml-6' : 'bg-white/5 text-gray-300 mr-6'}`}>
                  <p className="font-medium mb-1 text-gray-500">{m.from === 'business' ? 'Tú' : 'Soporte'}</p>
                  <p>{m.content}</p>
                </div>
              ))}
            </div>
          )}
          <div className="mt-4 border-t border-white/10 pt-4">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-xs">
              <div className="bg-white/5 rounded-lg p-2">
                <p className="text-gray-500">Tema</p>
                <p className="text-white mt-0.5">{selected.topic || selected.category}</p>
              </div>
              <div className="bg-white/5 rounded-lg p-2">
                <p className="text-gray-500">Creado</p>
                <p className="text-white mt-0.5">{fmtTime(selected.created_at)}</p>
              </div>
              <div className="bg-white/5 rounded-lg p-2">
                <p className="text-gray-500">Actualizado</p>
                <p className="text-white mt-0.5">{fmtTime(selected.updated_at)}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* SLA Panel */}
      <div className="glass-panel p-4 sm:p-5">
        <div className="flex items-center gap-2 mb-4">
          <Timer size={16} className="text-red-400" />
          <h4 className="text-white font-semibold text-sm">SLA — Acuerdos de Nivel de Servicio</h4>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {slaRules.map(r => (
            <div key={r.priority} className="bg-white/5 border border-white/10 rounded-xl p-3">
              <p className={`text-xs font-medium ${r.color}`}>{r.label}</p>
              <p className="text-white text-lg font-bold mt-1">{r.hours}h</p>
              <p className="text-gray-600 text-xs">tiempo máximo</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// ─── SETTINGS TAB ─────────────────────────────────────────────────────────────
const SettingsTab = ({ business }) => {
  const [section, setSection] = useState('agent'); // agent | notifications | profile
  const [profilePhoto, setProfilePhoto] = useState(business?.profile_photo || null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const photoRef = useRef(null);

  // Agent config state (mirrors AIAgentsManager)
  const [agentConfig, setAgentConfig] = useState({
    name: business?.name ? `Bot de ${business.name}` : 'Mi Bot IA',
    greeting: '¡Hola! ¿En qué puedo ayudarte?',
    system_prompt: `Eres un asistente virtual profesional para ${business?.name || 'este negocio'}. Responde de forma amable, concisa y útil. Siempre ofrece ayuda adicional al finalizar.`,
    color: '#ef4444',
    pexels_enabled: false,
    serpapi_enabled: false,
    auto_reply_enabled: true,
    offline_message: 'Gracias por contactarnos. Estamos fuera de horario. Te responderemos a la brevedad.',
    model_combo: 'free',
    response_delay_seconds: 1,
    skills: [],
  });
  const [agentTab, setAgentTab] = useState('general');
  const [savingAgent, setSavingAgent] = useState(false);
  const [loadingAgent, setLoadingAgent] = useState(true);

  // Notifications
  const [notifications, setNotifications] = useState({ whatsapp_enabled: false, whatsapp_number: '', email_enabled: false, email_address: '', daily_summary: true });
  const [savingNotif, setSavingNotif] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const [botRes, notifRes] = await Promise.all([
          axios.get(`${API}/business/${business.id}/bot-config`),
          axios.get(`${API}/business/${business.id}/notifications`).catch(() => ({ data: {} })),
        ]);
        if (botRes.data) setAgentConfig(p => ({ ...p, ...botRes.data }));
        if (notifRes.data) setNotifications(p => ({ ...p, ...notifRes.data }));
      } catch {}
      finally { setLoadingAgent(false); }
    };
    load();
  }, [business.id]);

  const setAgent = (key, val) => setAgentConfig(p => ({ ...p, [key]: val }));

  const saveAgent = async () => {
    setSavingAgent(true);
    try {
      await axios.put(`${API}/business/${business.id}/bot-config`, agentConfig);
      toast.success('Configuración del bot guardada');
    } catch { toast.error('Error al guardar'); }
    finally { setSavingAgent(false); }
  };

  const saveNotif = async () => {
    setSavingNotif(true);
    try { await axios.put(`${API}/business/${business.id}/notifications`, notifications); toast.success('Notificaciones guardadas'); }
    catch { toast.error('Error'); } finally { setSavingNotif(false); }
  };

  const handlePhoto = async (e) => {
    const f = e.target.files[0]; if (!f) return;
    if (f.size > 5 * 1024 * 1024) { toast.error('Máximo 5MB'); return; }
    setUploadingPhoto(true);
    try {
      const fd = new FormData(); fd.append('photo', f);
      const r = await axios.post(`${API}/business/${business.id}/profile-photo`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setProfilePhoto(r.data.photo_url); toast.success('Foto actualizada');
    } catch {
      const reader = new FileReader();
      reader.onload = ev => setProfilePhoto(ev.target.result);
      reader.readAsDataURL(f);
      toast.info('Foto guardada localmente');
    } finally { setUploadingPhoto(false); }
  };

  const COMBOS = [
    { id: 'free', label: 'Combo Esencial', icon: Unlock, desc: 'Modelos de alto rendimiento sin costo', color: 'text-green-400', bg: 'bg-green-500/15', border: 'border-green-500/30' },
    { id: 'paid', label: 'Combo Pro', icon: Star, desc: 'Claude, GPT-4o, Gemini Pro', color: 'text-yellow-400', bg: 'bg-yellow-500/15', border: 'border-yellow-500/30' },
    { id: 'super', label: 'Super Combo', icon: Sparkles, desc: 'Todos los modelos — óptimo automático', color: 'text-red-400', bg: 'bg-red-500/15', border: 'border-red-500/30' },
  ];

  const SKILLS_LIST = [
    { id: 'web_search', name: 'Búsqueda Web', icon: Globe },
    { id: 'image_search', name: 'Búsqueda de Imágenes', icon: Image },
    { id: 'calendar', name: 'Calendario', icon: Calendar },
    { id: 'finance', name: 'Finanzas', icon: BarChart3 },
    { id: 'weather', name: 'Clima', icon: Activity },
    { id: 'translate', name: 'Traducción', icon: Globe },
    { id: 'summarize', name: 'Resúmenes', icon: FileText },
    { id: 'code', name: 'Código', icon: Code },
  ];

  const toggleSkill = (id) => setAgent('skills', agentConfig.skills.includes(id) ? agentConfig.skills.filter(s => s !== id) : [...agentConfig.skills, id]);

  const SECTIONS = [
    { id: 'agent', label: 'Configurador de Bot', icon: Bot },
    { id: 'notifications', label: 'Notificaciones', icon: Bell },
    { id: 'profile', label: 'Perfil del Negocio', icon: Camera },
    { id: 'plans', label: 'Planes OkBot', icon: Crown },
  ];

  const AGENT_TABS = [
    { id: 'general', label: 'General' },
    { id: 'models', label: 'Modelos IA' },
    { id: 'skills', label: 'Habilidades' },
    { id: 'responses', label: 'Respuestas' },
  ];

  if (loadingAgent) return <div className="flex items-center justify-center h-64"><RefreshCw size={24} className="text-red-400 animate-spin" /></div>;

  return (
    <div className="flex flex-col lg:grid lg:grid-cols-4 gap-4 sm:gap-5">
      {/* Sidebar */}
      <div className="col-span-1 flex flex-row lg:flex-col gap-1 overflow-x-auto pb-1 lg:pb-0">
        {SECTIONS.map(s => {
          const Icon = s.icon;
          return (
            <button key={s.id} onClick={() => setSection(s.id)} className={`flex-shrink-0 lg:w-full flex items-center gap-2 px-3 py-2.5 lg:px-4 lg:py-3 rounded-xl text-xs sm:text-sm font-medium transition-colors text-left whitespace-nowrap ${section === s.id ? 'bg-red-500/15 text-red-400 border border-red-500/30' : 'text-gray-400 hover:text-white hover:bg-white/5'}`}>
              <Icon size={15} />{s.label}
            </button>
          );
        })}
      </div>

      {/* Content */}
      <div className="col-span-3">

        {section === 'agent' && (
          <div className="glass-panel p-5">
            <div className="flex items-center justify-between mb-5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: `${agentConfig.color}25` }}>
                  <Bot size={20} style={{ color: agentConfig.color }} />
                </div>
                <div>
                  <h3 className="text-white font-semibold">Configurador de Bot IA</h3>
                  <p className="text-gray-500 text-xs">Personaliza el comportamiento de tu asistente</p>
                </div>
              </div>
              <button onClick={saveAgent} disabled={savingAgent} className="px-4 py-2 bg-red-500 hover:bg-red-600 disabled:opacity-50 text-white text-sm rounded-xl font-medium transition-colors flex items-center gap-2">
                {savingAgent ? <RefreshCw size={13} className="animate-spin" /> : <Save size={13} />}Guardar
              </button>
            </div>

            <div className="flex gap-1 p-1 bg-white/5 rounded-xl mb-5">
              {AGENT_TABS.map(t => (
                <button key={t.id} onClick={() => setAgentTab(t.id)} className={`flex-1 py-1.5 rounded-lg text-xs font-medium transition-colors ${agentTab === t.id ? 'bg-red-500 text-white' : 'text-gray-400 hover:text-white'}`}>{t.label}</button>
              ))}
            </div>

            {agentTab === 'general' && (
              <div className="space-y-4">
                <div>
                  <label className="block text-gray-400 text-xs mb-1.5 font-medium">Nombre del Bot</label>
                  <input value={agentConfig.name} onChange={e => setAgent('name', e.target.value)} placeholder="Bot de Atención al Cliente"
                    className="input-glass w-full" />
                </div>
                <div>
                  <label className="block text-gray-400 text-xs mb-1.5 font-medium">Saludo Inicial</label>
                  <input value={agentConfig.greeting} onChange={e => setAgent('greeting', e.target.value)} placeholder="¡Hola! ¿En qué puedo ayudarte?"
                    className="input-glass w-full" />
                </div>
                <div>
                  <label className="block text-gray-400 text-xs mb-1.5 font-medium">System Prompt — Personalidad y función del bot</label>
                  <textarea value={agentConfig.system_prompt} onChange={e => setAgent('system_prompt', e.target.value)}
                    placeholder="Eres un asistente profesional de [negocio]. Tu objetivo es..."
                    className="input-glass w-full resize-none" rows={5} />
                  <p className="text-gray-600 text-xs mt-1">Define la personalidad, restricciones y objetivos del bot</p>
                </div>
                <div>
                  <label className="block text-gray-400 text-xs mb-2 font-medium">Color del Bot</label>
                  <div className="flex items-center gap-3">
                    <input type="color" value={agentConfig.color} onChange={e => setAgent('color', e.target.value)} className="w-10 h-10 rounded-lg cursor-pointer bg-transparent border-0" />
                    <div className="flex gap-2">
                      {COLORS.map(c => (
                        <button key={c} onClick={() => setAgent('color', c)} className="w-6 h-6 rounded-full border-2 transition-transform hover:scale-110" style={{ background: c, borderColor: agentConfig.color === c ? 'white' : 'transparent' }} />
                      ))}
                    </div>
                  </div>
                </div>
                <div className="flex gap-6">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={agentConfig.pexels_enabled} onChange={e => setAgent('pexels_enabled', e.target.checked)} className="w-4 h-4 accent-red-500" />
                    <span className="text-gray-300 text-sm flex items-center gap-1.5"><Image size={13} className="text-pink-400" />Pexels (Imágenes)</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={agentConfig.serpapi_enabled} onChange={e => setAgent('serpapi_enabled', e.target.checked)} className="w-4 h-4 accent-red-500" />
                    <span className="text-gray-300 text-sm flex items-center gap-1.5"><Search size={13} className="text-blue-400" />SerpAPI (Web)</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={agentConfig.auto_reply_enabled} onChange={e => setAgent('auto_reply_enabled', e.target.checked)} className="w-4 h-4 accent-red-500" />
                    <span className="text-gray-300 text-sm">Auto-respuesta</span>
                  </label>
                </div>
              </div>
            )}

            {agentTab === 'models' && (
              <div className="space-y-3">
                <p className="text-gray-400 text-sm font-medium mb-1">Modo de Modelos IA</p>
                {COMBOS.map(opt => {
                  const Icon = opt.icon;
                  const selected = agentConfig.model_combo === opt.id;
                  return (
                    <div key={opt.id} onClick={() => setAgent('model_combo', opt.id)} className={`flex items-center gap-4 p-4 rounded-xl border cursor-pointer transition-all ${selected ? `${opt.bg} ${opt.border}` : 'bg-white/3 border-white/8 hover:bg-white/6'}`}>
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${selected ? opt.bg : 'bg-white/10'}`}>
                        <Icon size={18} className={selected ? opt.color : 'text-gray-400'} />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <p className={`text-sm font-semibold ${selected ? opt.color : 'text-white'}`}>{opt.label}</p>
                        </div>
                        <p className="text-gray-500 text-xs mt-0.5">{opt.desc}</p>
                      </div>
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${selected ? `${opt.border} ${opt.color}` : 'border-gray-600'}`}>
                        {selected && <Check size={12} />}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {agentTab === 'skills' && (
              <div>
                <p className="text-gray-400 text-sm mb-4">Habilidades que tendrá tu bot</p>
                <div className="grid grid-cols-2 gap-2">
                  {SKILLS_LIST.map(skill => {
                    const sel = agentConfig.skills.includes(skill.id);
                    const Icon = skill.icon;
                    return (
                      <div key={skill.id} onClick={() => toggleSkill(skill.id)} className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-colors ${sel ? 'bg-red-500/15 border-red-500/40' : 'bg-white/3 border-white/5 hover:bg-white/8'}`}>
                        <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${sel ? 'bg-red-500 text-white' : 'bg-white/10 text-gray-400'}`}>
                          {sel ? <Check size={14} /> : <Icon size={13} />}
                        </div>
                        <span className="text-white text-xs font-medium">{skill.name}</span>
                      </div>
                    );
                  })}
                </div>
                {agentConfig.skills.length > 0 && <p className="text-gray-500 text-xs mt-3">{agentConfig.skills.length} habilidad{agentConfig.skills.length !== 1 ? 'es' : ''} activa{agentConfig.skills.length !== 1 ? 's' : ''}</p>}
              </div>
            )}

            {agentTab === 'responses' && (
              <div className="space-y-4">
                <div>
                  <label className="block text-gray-400 text-xs mb-1.5 font-medium">Mensaje de bienvenida</label>
                  <textarea value={agentConfig.greeting} onChange={e => setAgent('greeting', e.target.value)} rows={2} className="input-glass w-full resize-none" />
                </div>
                <div>
                  <label className="block text-gray-400 text-xs mb-1.5 font-medium">Mensaje fuera de horario</label>
                  <textarea value={agentConfig.offline_message} onChange={e => setAgent('offline_message', e.target.value)} rows={3} className="input-glass w-full resize-none" />
                </div>
                <div>
                  <label className="block text-gray-400 text-xs mb-1.5 font-medium">Retraso de respuesta (segundos)</label>
                  <input type="number" min={0} max={10} value={agentConfig.response_delay_seconds} onChange={e => setAgent('response_delay_seconds', Number(e.target.value))} className="input-glass w-32" />
                  <p className="text-gray-600 text-xs mt-1">Simula tiempo de escritura humano</p>
                </div>
              </div>
            )}
          </div>
        )}

        {section === 'notifications' && (
          <div className="glass-panel p-5 space-y-5">
            <h3 className="text-white font-semibold flex items-center gap-2"><Bell size={16} className="text-red-400" />Notificaciones</h3>
            {[
              { key: 'whatsapp_enabled', icon: Phone, label: 'WhatsApp', desc: 'Notificaciones de nuevos chats por WhatsApp', inputKey: 'whatsapp_number', placeholder: '+52 55 0000 0000', inputType: 'tel', color: 'text-green-400' },
              { key: 'email_enabled', icon: Mail, label: 'Email', desc: 'Resumen diario y alertas por email', inputKey: 'email_address', placeholder: 'correo@empresa.com', inputType: 'email', color: 'text-blue-400' },
            ].map(n => {
              const Icon = n.icon;
              return (
                <div key={n.key} className="p-4 bg-white/3 border border-white/10 rounded-xl space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-9 h-9 rounded-xl bg-white/5 flex items-center justify-center`}>
                        <Icon size={16} className={n.color} />
                      </div>
                      <div>
                        <p className="text-white font-medium text-sm">{n.label}</p>
                        <p className="text-gray-500 text-xs">{n.desc}</p>
                      </div>
                    </div>
                    <button onClick={() => setNotifications(p => ({ ...p, [n.key]: !p[n.key] }))} className="flex-shrink-0">
                      {notifications[n.key] ? <ToggleRight size={24} className="text-green-400" /> : <ToggleLeft size={24} className="text-gray-600" />}
                    </button>
                  </div>
                  {notifications[n.key] && (
                    <input type={n.inputType} value={notifications[n.inputKey] || ''} onChange={e => setNotifications(p => ({ ...p, [n.inputKey]: e.target.value }))}
                      placeholder={n.placeholder} className="w-full bg-black/20 border border-white/10 rounded-xl px-4 py-2.5 text-white text-sm placeholder-gray-600 focus:outline-none focus:border-red-400" />
                  )}
                </div>
              );
            })}
            <div className="p-4 bg-white/3 border border-white/10 rounded-xl flex items-center justify-between">
              <div>
                <p className="text-white font-medium text-sm">Resumen diario</p>
                <p className="text-gray-500 text-xs">Estadísticas automáticas cada día</p>
              </div>
              <button onClick={() => setNotifications(p => ({ ...p, daily_summary: !p.daily_summary }))}>
                {notifications.daily_summary ? <ToggleRight size={24} className="text-green-400" /> : <ToggleLeft size={24} className="text-gray-600" />}
              </button>
            </div>
            <button onClick={saveNotif} disabled={savingNotif} className="w-full py-2.5 bg-red-500 hover:bg-red-600 disabled:opacity-50 text-white rounded-xl text-sm font-medium transition-colors flex items-center justify-center gap-2">
              {savingNotif ? <RefreshCw size={14} className="animate-spin" /> : <Save size={14} />}Guardar Notificaciones
            </button>
          </div>
        )}

        {section === 'profile' && (
          <div className="glass-panel p-5 space-y-5">
            <h3 className="text-white font-semibold flex items-center gap-2"><Camera size={16} className="text-red-400" />Perfil del Negocio</h3>
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-6 p-4 sm:p-5 bg-white/3 border border-white/10 rounded-xl">
              <div className="relative">
                <div className="w-24 h-24 rounded-2xl overflow-hidden border-2 border-white/10 bg-white/5 flex items-center justify-center">
                  {profilePhoto ? <img src={profilePhoto} alt="Perfil" className="w-full h-full object-cover" /> : (
                    <div className="flex flex-col items-center gap-1 text-gray-600"><Camera size={24} /><span className="text-xs">Sin foto</span></div>
                  )}
                </div>
                {uploadingPhoto && <div className="absolute inset-0 bg-black/50 rounded-2xl flex items-center justify-center"><RefreshCw size={18} className="text-white animate-spin" /></div>}
              </div>
              <div className="flex flex-col gap-3">
                <div>
                  <p className="text-white font-medium text-sm">{business?.name}</p>
                  <p className="text-gray-500 text-sm">{business?.email}</p>
                </div>
                <p className="text-gray-500 text-xs">PNG, JPG o WebP · Máx 5MB</p>
                <div className="flex gap-2">
                  <button onClick={() => photoRef.current?.click()} disabled={uploadingPhoto} className="flex items-center gap-2 px-4 py-2 bg-red-500 hover:bg-red-600 disabled:opacity-50 text-white text-sm rounded-lg font-medium transition-colors">
                    <Upload size={13} />{uploadingPhoto ? 'Subiendo...' : 'Subir Foto'}
                  </button>
                  {profilePhoto && <button onClick={() => setProfilePhoto(null)} className="flex items-center gap-2 px-3 py-2 bg-white/10 hover:bg-white/20 text-white text-sm rounded-lg transition-colors"><X size={13} /></button>}
                </div>
                <input ref={photoRef} type="file" accept="image/*" className="hidden" onChange={handlePhoto} />
              </div>
            </div>
          </div>
        )}

        {section === 'plans' && (
          <div className="glass-panel p-5">
            <PlansModule currentUser={{ id: business?.id, plan: business?.plan || 'free', role: 'business' }} />
          </div>
        )}
      </div>
    </div>
  );
};

// ─── MAIN BUSINESS DASHBOARD ─────────────────────────────────────────────────
const BusinessDashboard = ({ business }) => {
  const [activeTab, setActiveTab] = useState('chats');
  const [showSupportChat, setShowSupportChat] = useState(false);

  const tabs = [
    { id: 'chats', label: 'Chats', icon: MessageSquare, color: 'text-blue-400' },
    { id: 'analytics', label: 'Analytics', icon: BarChart3, color: 'text-purple-400' },
    { id: 'schedule', label: 'Horario & Automaciones', icon: Clock, color: 'text-yellow-400' },
    { id: 'support', label: 'Soporte', icon: Headphones, color: 'text-green-400' },
    { id: 'settings', label: 'Configuración', icon: Settings, color: 'text-red-400' },
    { id: 'okbot', label: 'OkBot IA', icon: Bot, color: 'text-orange-400' },
  ];

  return (
    <div className="p-3 sm:p-5 lg:p-6" data-testid="business-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between mb-4 sm:mb-6 gap-2">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-2xl bg-gradient-to-br from-red-500/20 to-orange-500/10 border border-red-500/20 flex items-center justify-center flex-shrink-0">
            <img src="/okbot_logo_2026.png" alt="OkBot" style={{ width: 24, height: 24, objectFit: 'contain' }} />
          </div>
          <div className="min-w-0">
            <h1 className="text-base sm:text-xl font-bold text-white truncate">{business.name}</h1>
            <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
              <span className={`w-2 h-2 rounded-full flex-shrink-0 ${business.is_active ? 'bg-green-400' : 'bg-red-400'}`} />
              <span className={`text-xs ${business.is_active ? 'text-green-400' : 'text-red-400'}`}>{business.is_active ? 'Activo' : 'Inactivo'}</span>
              <span className="text-gray-600 text-xs hidden sm:inline">·</span>
              <span className="text-gray-500 text-xs hidden sm:inline truncate">{business.email}</span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <button onClick={() => setShowSupportChat(true)} className="p-2 sm:px-3 sm:py-2 bg-blue-500/15 hover:bg-blue-500/25 text-blue-400 border border-blue-500/30 rounded-xl text-sm font-medium transition-colors flex items-center gap-2" data-testid="business-support-btn">
            <Headphones size={14} /><span className="hidden sm:inline">Soporte OkGo</span>
          </button>
        </div>
      </div>

      {/* Tabs — scrollable on small screens, wraps label on mobile */}
      <div className="flex gap-1 mb-4 sm:mb-6 p-1 bg-white/3 border border-white/10 rounded-2xl overflow-x-auto scrollbar-thin">
        {tabs.map(t => {
          const Icon = t.icon;
          return (
            <button key={t.id} onClick={() => setActiveTab(t.id)} data-testid={`tab-${t.id}`}
              className={`flex items-center gap-1.5 px-3 py-2 sm:px-4 sm:py-2.5 rounded-xl text-xs sm:text-sm font-medium transition-all whitespace-nowrap flex-shrink-0 ${activeTab === t.id ? `bg-white/10 text-white shadow` : 'text-gray-500 hover:text-gray-300'}`}>
              <Icon size={13} className={activeTab === t.id ? t.color : ''} />
              <span className="hidden xs:inline sm:inline">{t.label}</span>
            </button>
          );
        })}
      </div>

      {/* Tab Content */}
      {activeTab === 'chats' && <ChatsTab business={business} />}
      {activeTab === 'analytics' && <AnalyticsTab business={business} />}
      {activeTab === 'schedule' && <ScheduleTab business={business} />}
      {activeTab === 'support' && <SupportTab business={business} />}
      {activeTab === 'settings' && <SettingsTab business={business} />}
      {activeTab === 'okbot' && <OkBotChatTab business={business} />}

      {/* Support Chat Modal */}
      {showSupportChat && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
          <div className="w-full max-w-md h-[600px] bg-surface rounded-2xl flex flex-col overflow-hidden shadow-2xl border border-white/10">
            <div className="flex items-center justify-between p-4 bg-blue-500">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-white/20 flex items-center justify-center"><Headphones size={18} className="text-white" /></div>
                <div><p className="text-white font-semibold text-sm">Soporte OkGo Negocios</p><p className="text-blue-100 text-xs">Asistente virtual</p></div>
              </div>
              <button onClick={() => setShowSupportChat(false)} className="p-1.5 hover:bg-white/20 rounded-lg transition-colors"><X size={18} className="text-white" /></button>
            </div>
            <div className="flex-1 flex items-center justify-center p-8 text-center">
              <div>
                <Headphones size={32} className="text-blue-400 mx-auto mb-3" />
                <p className="text-white font-medium">Soporte en línea</p>
                <p className="text-gray-400 text-sm mt-1">Usa la pestaña "OkBot IA" para chatear con el asistente, o crea un ticket en "Soporte"</p>
                <button onClick={() => { setShowSupportChat(false); setActiveTab('support'); }} className="mt-4 px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-xl text-sm font-medium transition-colors">
                  Ir a Soporte
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BusinessDashboard;
